import { Dayjs } from "dayjs";
import { CalendarEventModel } from "./CalendarEvent.model";

export class CalendarEvent extends CalendarEventModel {
    static blankUnavailabilityEvent(
        projectId: number,
        startAt: Dayjs | null,
        endAt: Dayjs | null,
        timezone: string
    ): CalendarEvent {
        const newCalendarEvent = CalendarEvent.fromApiObject(
            {
                archived: false,
                project_id: projectId,
                calendar_event_type: "unavailability",
                name: null, // NOTE: This WILL throw errors in the backend if not given a value before POST
            },
            timezone
        );

        newCalendarEvent.startAt = startAt;
        newCalendarEvent.endAt = endAt;

        return newCalendarEvent;
    }

    eventType() {
        if (this.calendarableType) {
            return this.calendarableType;
        }

        return "Event";
    }

    deliveryReceivingTeamId(): number | null {
        // If this event is not a delivery, there is no relevant id
        if (this.calendarableType !== "Delivery") {
            return null;
        }

        const possibleTeams = this.calendarEventResources?.filter(
            (resource) => resource.calendarResourceableType === "Team" && resource.deliveryRole === "team"
        );

        if (!possibleTeams || possibleTeams.length === 0) {
            return null;
        }

        // A delivery "should" only have 1 receiving team, but if there are multiple, just return the first one
        // This is mostly a check because our data structure allows for multiple teams
        return possibleTeams[0].calendarResourceableId;
    }

    deliveryDropOffId(): number | null {
        if (this.calendarableType !== "Delivery") {
            return null;
        }

        const possibleDropOffs = this.calendarEventResources?.filter(
            (resource) => resource.calendarResourceableType === "Area" && resource.deliveryRole === "drop_off"
        );

        if (!possibleDropOffs || possibleDropOffs.length === 0) {
            return null;
        }

        // A delivery "should" only have 1 drop off, but if there are multiple, just return the first one
        // This is mostly a check because our data structure allows for multiple drop offs
        return possibleDropOffs[0].calendarResourceableId;
    }

    deliveryWaypointId(): number | null {
        if (this.calendarableType !== "Delivery") {
            return null;
        }

        const possibleWaypoints = this.calendarEventResources?.filter(
            (resource) => resource.calendarResourceableType === "Area" && resource.deliveryRole === "waypoint"
        );

        if (!possibleWaypoints || possibleWaypoints.length === 0) {
            return null;
        }

        // A delivery "should" only have 1 waypoint, but if there are multiple, just return the first one
        // This is mostly a check because our data structure allows for multiple waypoints
        return possibleWaypoints[0].calendarResourceableId;
    }

    deliveryVendorId(): number | null {
        if (this.calendarableType !== "Delivery") {
            return null;
        }

        const possibleVendors = this.calendarEventResources?.filter(
            (resource) => resource.calendarResourceableType === "Team" && resource.deliveryRole === "vendor"
        );

        if (!possibleVendors || possibleVendors.length === 0) {
            return null;
        }

        // A delivery "should" only have 1 vendor, but if there are multiple, just return the first one
        // This is mostly a check because our data structure allows for multiple vendors
        return possibleVendors[0].calendarResourceableId;
    }

    deliveryVendorUserId(): number | null {
        if (this.calendarableType !== "Delivery") {
            return null;
        }

        const possibleVendorUsers = this.calendarEventResources?.filter(
            (resource) => resource.calendarResourceableType === "User" && resource.deliveryRole === "vendor_user"
        );

        if (!possibleVendorUsers || possibleVendorUsers.length === 0) {
            return null;
        }

        // A delivery "should" only have 1 vendor user, but if there are multiple, just return the first one
        // This is mostly a check because our data structure allows for multiple vendor users
        return possibleVendorUsers[0].calendarResourceableId;
    }

    deliveryEquipmentIds(): number[] {
        if (this.calendarableType !== "Delivery") {
            return [];
        }

        const possibleEquipments = this.calendarEventResources?.filter(
            (resource) => resource.calendarResourceableType === "Equipment" && resource.deliveryRole === "equipment"
        );

        if (!possibleEquipments || possibleEquipments.length === 0) {
            return [];
        }

        return possibleEquipments.map((resource) => resource.calendarResourceableId);
    }

    get duration(): number {
        return this.endAt.diff(this.startAt, "minutes");
    }
}
